import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { userApi } from '@/api/admin/users';
import { AuditModal } from '@/components/modal/admin/user/audit-modal';
import { UserTable } from '@/components/table/admin/user-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useAuditModal } from '@/hooks/use-audit-modal';

export function UserListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Audit modal hook
  const {
    isOpen: isAuditModalOpen,
    user: auditUser,
    currentPage: auditCurrentPage,
    auditData,
    isLoading: isAuditLoading,
    openModal: openAuditModal,
    closeModal: closeAuditModal,
    handlePageChange: handleAuditPageChange,
  } = useAuditModal();

  // Filter states
  const [filterName, setFilterName] = useState(searchParams.get('name') || '');
  const [filterCpf, setFilterCpf] = useState(searchParams.get('cpf') || '');
  const [filterEmail, setFilterEmail] = useState(searchParams.get('email') || '');
  const [filterUsername, setFilterUsername] = useState(searchParams.get('username') || '');

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', Object.fromEntries(searchParams)],
    queryFn: () => userApi.findMany(Object.fromEntries(searchParams) as Record<string, string>),
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterName) params.set('name', filterName);
    if (filterCpf) params.set('cpf', filterCpf);
    if (filterEmail) params.set('email', filterEmail);
    if (filterUsername) params.set('username', filterUsername);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterCpf('');
    setFilterEmail('');
    setFilterUsername('');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar usuários</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Usuários</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os usuários registrados na plataforma.
          </p>
        </div>
        <Button asChild className="bg-black text-white hover:bg-gray-800">
          <Link to="/admin/users/create">
            <Plus className="h-4 w-4 mr-2" />
            Novo Usuário
          </Link>
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Input
                  label="Nome"
                  placeholder="Filtrar por nome"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="CPF"
                  placeholder="Filtrar por CPF"
                  value={filterCpf}
                  onChange={(e) => setFilterCpf(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Email"
                  placeholder="Filtrar por email"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Usuário"
                  placeholder="Filtrar por usuário"
                  value={filterUsername}
                  onChange={(e) => setFilterUsername(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <UserTable
          data={data}
          currentPage={page}
          onPageChange={handlePageChange}
          onOpenAuditModal={openAuditModal}
        />
      ) : null}

      {/* Audit Modal */}
      <AuditModal
        open={isAuditModalOpen}
        onClose={closeAuditModal}
        userName={auditUser?.name ?? ''}
        auditData={auditData}
        currentPage={auditCurrentPage}
        onPageChange={handleAuditPageChange}
        isLoading={isAuditLoading}
      />
    </motion.div>
  );
}
